/**
 * Tax-rate (GST/VAT) math + labels — the FIRST shared client/server module
 * (overhaul Wave 1). One implementation, consumed by:
 *   client: src/utils/taxRate.ts + src/services/quote-pricing.ts (re-export)
 *   server: functions/src/util/tenant-tax-rate.ts (re-export shim)
 *
 * Replaces the hand-mirrored pair that had already drifted (client label was
 * always "GST (X%)"; server banded GST/VAT/Tax — KNOWN_ISSUES L-1). The
 * UNIFIED behaviour adopts the server's banding with the client's precision
 * (trailing-zero trim, no lossy rounding): identical output at 10%, and a
 * consistent word on proposal AND invoice for any other rate.
 *
 * PURITY CONTRACT (lint-enforced for all of shared/): no React, no firebase,
 * no node builtins, no ambient browser/server state. Rates are DECIMALS
 * (0.1 = 10%); stored money stays ex-GST — these helpers power display
 * grossing-up and labels only.
 */

/** Platform default GST rate — Australian GST is 10%. */
export const DEFAULT_TENANT_TAX_RATE = 0.1;

/** A valid tax rate is a finite decimal in [0, 1]. */
export function isValidTaxRate(rate: unknown): rate is number {
  return typeof rate === 'number' && Number.isFinite(rate) && rate >= 0 && rate <= 1;
}

/**
 * Sanitize an untrusted GST/tax rate into a valid DECIMAL rate. Healing
 * rules (2026-07 pricing audit), identical on both sides:
 *   - numeric strings parse ('0.1' → 0.1)
 *   - decimals in [0, 1] pass through
 *   - values in (1, 100] are percent-form and divide by 100 (10 → 0.10)
 *   - everything else (negative, > 100, NaN, non-numeric) → fallback
 *
 * Why healing matters: a malformed rate reaching a `value * (1 + rate)`
 * gross-up inflates customer-facing money ~×10 (string concat) or ×11
 * (percent-form) — this bit the proposal monthly figures.
 *
 * `onWarn` carries the client's diagnostic console.warns (quote-pricing
 * passes a logger; the server stays silent, matching prior behaviour).
 */
export function sanitizeGstRate(
  rate: unknown,
  fallback: number = DEFAULT_TENANT_TAX_RATE,
  onWarn?: (message: string) => void,
): number {
  let n: number | null = null;
  if (typeof rate === 'number') {
    n = rate;
  } else if (typeof rate === 'string' && rate.trim() !== '') {
    n = Number(rate.trim());
    if (Number.isFinite(n)) {
      onWarn?.(
        `GST rate arrived as a string ("${rate}") — parsed to ${n}. ` +
          'Fix the source field to a number.',
      );
    }
  }
  if (n === null || !Number.isFinite(n)) {
    if (rate !== undefined && rate !== null) {
      onWarn?.(`Invalid GST rate ${JSON.stringify(rate)} — using fallback ${fallback}.`);
    }
    return fallback;
  }
  if (n >= 0 && n <= 1) return n;
  if (n > 1 && n <= 100) {
    onWarn?.(
      `GST rate ${n} looks percent-form — interpreting as ${n / 100}. ` +
        'Store the decimal form (e.g. 0.1 for 10%).',
    );
    return n / 100;
  }
  onWarn?.(`GST rate ${n} is out of range — using fallback ${fallback}.`);
  return fallback;
}

/**
 * Resolve a (possibly missing/malformed) stored rate to a valid decimal.
 * Valid rates pass through; recoverable malformed values heal via
 * sanitizeGstRate; unrecoverable values fall back to the platform default.
 */
export function resolveTaxRate(rate: unknown): number {
  return isValidTaxRate(rate) ? rate : sanitizeGstRate(rate, DEFAULT_TENANT_TAX_RATE);
}

/**
 * Percentage string for a decimal rate with trailing zeros trimmed and no
 * lossy rounding: 0.1 → "10", 0.125 → "12.5", 0.1234 → "12.34".
 */
export function taxRatePercentString(rate: number): string {
  const pct = isValidTaxRate(rate) ? rate * 100 : DEFAULT_TENANT_TAX_RATE * 100;
  return Number.isInteger(pct) ? String(pct) : String(Number(pct.toFixed(2)));
}

/**
 * The bare tax WORD for a rate, no percentage — "GST" / "VAT" / "Tax".
 * Band rules identical to buildTaxLabel (which is built on this):
 *   - ~10% (AU/NZ-style) → GST
 *   - 15–25% (EU/UK-style) → VAT
 *   - anything else → Tax
 *
 * For tight UI chrome where the percentage is shown separately or would
 * not fit (segmented toggles, per-line switches, column headers).
 */
export function taxWordForRate(rate: unknown): string {
  const r = resolveTaxRate(rate);
  if (Math.abs(r - 0.1) < 1e-9) return 'GST';
  if (r >= 0.15 && r <= 0.25) return 'VAT';
  return 'Tax';
}

/**
 * Human-readable tax label, e.g. "GST (10%)" / "VAT (20%)" / "Tax (7%)".
 * Word picked by rate band so a non-AU tenant gets the right term on BOTH
 * the proposal and the invoice.
 */
export function buildTaxLabel(rate: unknown): string {
  const r = resolveTaxRate(rate);
  return `${taxWordForRate(r)} (${taxRatePercentString(r)}%)`;
}
